import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import type { MarketSnapshotsKpiTotal } from 'lumen/components/market/snapshots/kpi-card';
import type { Kpi } from 'lumen/models/market-performance/data-item';
import type MarketPerformanceKpi from 'lumen/models/market-performance/kpi';
import {
  type ComparisonMetricType,
  METRIC_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/market-snapshots-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';

export interface KpiCardData {
  title: string;
  total: MarketSnapshotsKpiTotal;
  variance: number | null;
}

interface KpiCardOptions {
  comparisonMetricType: ComparisonMetricType;
  varianceType: Variance;
}

const KPI_CARD_CONFIGS: Record<
  Kpi,
  { format: MarketSnapshotsKpiTotal['format']; titleKey: string }
> = {
  adr: { format: 'currency', titleKey: tKey('general.adr') },
  occupancy: { format: 'percent', titleKey: tKey('general.occupancy') },
  revenue: { format: 'currency', titleKey: tKey('general.revenue') },
  revpar: { format: 'currency', titleKey: tKey('general.revpar') },
  rooms: { format: 'decimal', titleKey: tKey('general.room_nights') },
};

/**
 * Picks one KPI card's data from the KPI records: the translated title, the
 * OTB total and its variance against the comparison metric type. Missing data
 * is null, never 0.
 */
export function buildKpiCardData(
  intl: Services['intl'],
  kpis: MarketPerformanceKpi[],
  kpiName: Kpi,
  { comparisonMetricType, varianceType }: KpiCardOptions,
): KpiCardData {
  const { format, titleKey } = KPI_CARD_CONFIGS[kpiName];
  const varianceKey =
    varianceType === VARIANCES.ABSOLUTE ? 'absolute' : 'percent';
  const otb = kpis.find(({ id }) => id === 'otb');
  const comparisonKpi = kpis.find(({ id }) => id === comparisonMetricType);

  return {
    title: intl.t(titleKey),
    total: { format, value: otb?.totals?.[kpiName] ?? null },
    variance: comparisonKpi?.totals?.variance?.[kpiName][varianceKey] ?? null,
  };
}

// The variance line label shared by the KPI cards, e.g. "vs. SDLY".
export function buildVarianceLabel(
  intl: Services['intl'],
  comparisonMetricType: ComparisonMetricType,
): string {
  return intl.t('general.versus.option', {
    option: intl.t(METRIC_TYPE_TRANSLATIONS[comparisonMetricType]),
  });
}
